import { createAsyncThunk } from '@reduxjs/toolkit';
import { Auth } from "aws-amplify";
import { confirmSignIn } from '@aws-amplify/auth';
import awsconfig from '../../aws-config';
import axios from 'axios';
import { compareDate, errorAlert } from '../../utils';
import { doc } from 'prettier';
import { showAlertSuccErr } from '../../utils/alert';

Auth.configure({
  ...awsconfig,
  authenticationFlowType: "CUSTOM_AUTH",
  storage: sessionStorage,
});

const BASE_URL = `${process.env.REACT_APP_SESSION_API_URL}/auth`;

export const loginSession = createAsyncThunk(
  "session/login",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await Auth.signIn({
        username,
        password
      });
      return response;
    } catch (error) {
      if (error.code === 'UserDisabledException') {
        // Usuario deshabilitado en Cognito
        return rejectWithValue('Tu cuenta está deshabilitada. Por favor contacta al administrador.');
      }
      console.error("Error durante el inicio de sesion: ", error);
      return rejectWithValue(error.message || 'Error al iniciar sesión');
    }
  }
);

export const loginSessionLdap = createAsyncThunk(
  "session/loginLdap",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await Auth.signIn({
        username: username,
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
        },
      });

      const responseData = await Auth.sendCustomChallengeAnswer(response, JSON.stringify({ username, password }));
      return responseData;
    } catch (error) {
      if (error.code === 'UserDisabledException') {
        return rejectWithValue('User is disabled.');
      }
      console.error("Error durante el inicio de sesión: ", error);
      return rejectWithValue(error.message || 'Error al iniciar sesión');
    }
  }
);

export const registerUser = createAsyncThunk(
  "session/register",
  async (data, { rejectWithValue }) => {
    const { email, password, document, firstname, lastname, nit } = data;
    try {
      const body = {
        email: email,
        password: password,
        document: document,
        nit: nit,  
        firstname: firstname,
        lastname: lastname,
      };
      const response = await axios.post(`${BASE_URL}/registerUser`, body);
      return response;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error.response.data);
    }
  }
);

export const confirmRegisterCode = createAsyncThunk(
  "session/confirmRegister",
  async (data, { rejectWithValue }) => {
    const { email, code } = data;
    try {
      const body = {
        email: email,
        code: code,
      };
      const response = await axios.post(`${BASE_URL}/validate`, body);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const confirmLoginCode = createAsyncThunk("session/confirmLogin", async (code, thunkAPI) => {
  try {
    const { session } = thunkAPI.getState().session;
    const response = await Auth.sendCustomChallengeAnswer(session, code);
    const alertData = compareDate(response.attributes?.updated_at);
    // if (alertData.refresh) {
    //   return { refresh: true };
    // }
    if (Object.keys(response.signInUserSession).length !== 0) {
      const user = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      return { ...user, ...alertData };
    }
    return { ...response, ...alertData };
  } catch (error) {
    console.error("Error durante la autenticación: ", error);
    throw error;
  }
});

export const logoutSession = createAsyncThunk("session/logout", async () => {
  const response = await Auth.signOut();
  return response;
});

export const changePassword = createAsyncThunk(
  "session/changePassword",
  async ({ newPassword, oldPassword }) => {
    try {
      const user = await getCurrentUser();
      const response = await updatePassword({user, oldPassword, newPassword});
      await updateUserAttributes(user, {
        updated_at: new Date(new Date().getTime() + 7776000000).getTime().toString(),
      });
      return response;
    } catch (error) {
      console.error("Error durante el envio de código para cambio de password: ", error);
      throw error;
    }
  }
);

export const resenCodeRegister = createAsyncThunk(
  "session/resendCodeRegister",
  async ({ username }, { rejectWithValue }) => {
    try {
      const body = {
        docNumber: username,
      };
      const response = await axios.post(`${BASE_URL}/session/resendCode`, body);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const confirmNewPassword = createAsyncThunk(
  "session/confirmNewPassword",
  async (password, { getState }) => {
    try {
      const { session } = getState().session;
      const response = await Auth.completeNewPassword(session, password);
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        updated_at: new Date(new Date().getTime() + 7776000000).getTime().toString(),
      });
      return response;
    } catch (error) {
      console.error("Error durante el envio de código para cambio de password: ", error);
      throw error;
    }
  }
);

export const sendCodeRecoverPassword = createAsyncThunk(
  "session/sendCodeRecoverPassword",
  async (username) => {
    try {
      const response = await Auth.forgotPassword(username);

      return response;
    } catch (error) {
      console.error("Error durante el envio de código para cambio de password: ", error);
      throw error;
    }
  }
);

export const confirmCodeRecoverPassword = createAsyncThunk(
  "session/confirmCodeRecoverPassword",
  async ({ username, code, password }) => {
    try {
      const response = await Auth.forgotPasswordSubmit(username, code, password);
      const user = await Auth.currentAuthenticatedUser();
      await Auth.updateUserAttributes(user, {
        updated_at: new Date(new Date().getTime() + 7776000000).getTime().toString(),
      });
      return response;
    } catch (error) {
      console.error("Error durante el envio de código para cambio de password: ", error);
      throw error;
    }
  }
);

export const setInfoUser = createAsyncThunk('session/setInfoUser', async () => {
  try {
    const token = sessionStorage.getItem("token");
    const user = sessionStorage.getItem("user");
    const role = sessionStorage.getItem("role");
    return {
      token,
      user: JSON.parse(user),
      role
    }
  } catch (error) {
    console.error(error);
    showAlertSuccErr({
      title: error.response.data,
      icon: 'error'
    })
    throw error;
  }
});

export const logout = createAsyncThunk('session/logout', async () => {
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("user");
  sessionStorage.removeItem("role");
  return
});

export const authMassive = createAsyncThunk(
  "session/authMassive",
  async (data, { rejectWithValue }) => {
    const formData = new FormData();
    formData.append('file', data.excelFile);
    try {
      const response = await axios.post(`${BASE_URL}/authMassive`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error.response.data);
    }
  }
);

export const authMassiveCompanies = createAsyncThunk(
  "session/registerCompanyMassive",
  async (data, { rejectWithValue }) => {
    const formData = new FormData();
    formData.append('file', data.excelFile);
    try {
      const response = await axios.post(`${BASE_URL}/registerCompanyMassive`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return rejectWithValue(error.response.data);
    }
  }
);

export const registerUserMassive = createAsyncThunk(
  "session/registerUserMassive",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/registerUserMassive`, {data: data}, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': sessionStorage.getItem('token')
        }
      });
      return response.data;
    } catch (error) {
      console.error(error);
      errorAlert(error?.response?.data?.message?.toUpperCase() || 'Error en el registro');
      return rejectWithValue(error.response.data);
    }
  }
);

export const registerCompaniesMassive = createAsyncThunk(
  "session/registerCompaniesMassive",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/registerCompaniesMassive`, {data: data}, {
        headers: {
          'Content-Type': 'application/json',
          'authorization': sessionStorage.getItem('token')
        }
      });
      return response.data;
    } catch (error) {
      console.error(error);
      errorAlert(error?.response?.data?.message?.toUpperCase() || 'Error en el registro');
      return rejectWithValue(error.response.data);
    }
  }
);
